import { useState } from "react";
import { Modal } from "~/components/Modal";
import type { OrderDocumentSlug } from "~/shared/documents/order-document-settings";
import { Menu } from "~/ui";
import { filterVisibleOptInOrderDocuments, filterVisibleOptInPurchaseOrderDocuments } from "./gating";
import { OPT_IN_ORDER_DOCUMENT_DEFINITIONS } from "./registry";
import type { OrderMenuDocumentExtensionContext } from "./types";

/**
 * Generic opt-in order document menu + modals — one modal per slug from registry.
 * @see docs/OPT_IN_ORDER_DOCUMENTS.md
 */
export const useOptInOrderDocumentExtension = (context: OrderMenuDocumentExtensionContext) => {
  const { order, hasUpdatePermission, getOrgSetting, orgBaseCurrencyCode, onSuccess } = context;
  const [activeSlug, setActiveSlug] = useState<OrderDocumentSlug | null>(null);
  const closeModal = () => setActiveSlug(null);

  const handleSuccess = () => {
    closeModal();
    onSuccess();
  };

  const commonGating = {
    definitions: OPT_IN_ORDER_DOCUMENT_DEFINITIONS,
    getOrgSetting,
    orgBaseCurrencyCode,
    hasUpdatePermission,
  };
  const visibleDefinitions =
    context.sourceType === "purchaseOrder"
      ? filterVisibleOptInPurchaseOrderDocuments({ ...commonGating, purchaseOrder: context.order })
      : filterVisibleOptInOrderDocuments({
          ...commonGating,
          orderStatus: context.order.status,
          orderEligibility: { type: context.order.type, purchaseOrderId: context.order.purchaseOrderId ?? null },
        });

  const activeDefinition = visibleDefinitions.find(definition => definition.id === activeSlug);
  const ActiveForm = activeDefinition?.FormComponent;

  const menuItems = (
    <>
      {visibleDefinitions.map(definition => (
        <Menu.Item key={definition.id} onClick={() => setActiveSlug(definition.id)}>
          {definition.menuItem.label}
        </Menu.Item>
      ))}
    </>
  );

  const modals =
    activeDefinition && ActiveForm && order ? (
      <Modal opened onClose={closeModal} title={activeDefinition.menuItem.label} size="xl">
        <ActiveForm
          key={`${context.sourceType ?? "order"}:${order.id}`}
          orderId={order.id}
          sourceType={context.sourceType}
          onSuccess={handleSuccess}
          onCancel={closeModal}
        />
      </Modal>
    ) : null;

  return { menuItems, modals };
};
